import React, { useEffect, useRef, useState, useMemo } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, MessageCircle, Send, Search, AlertCircle, CheckCheck, Check, Plus, RefreshCw, Sparkles, Trash2, Paperclip, FileText, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

/** WhatsApp — bandeja unificada estilo chat (lista izda + hilo dcha). */
export default function WhatsApp() {
  const [convs, setConvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState("");
  const [showCompose, setShowCompose] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/whatsapp/conversations");
      setConvs(data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error cargando conversaciones");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); const id = setInterval(load, 15_000); return () => clearInterval(id); }, []);

  const deleteConversation = async (c) => {
    const label = c.contact_name || c.contact_profile_name || `+${c.contact_wa_id}`;
    if (!window.confirm(`¿Eliminar la conversación con ${label}?\n\nSe borrarán todos sus mensajes. No se puede deshacer.`)) return;
    try {
      await api.delete(`/whatsapp/conversations/${c.id}`);
      toast.success("Conversación eliminada");
      if (selectedId === c.id) setSelectedId(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al eliminar");
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return convs;
    return convs.filter((c) =>
      (c.contact_wa_id || "").includes(q) ||
      (c.contact_name || "").toLowerCase().includes(q) ||
      (c.contact_profile_name || "").toLowerCase().includes(q) ||
      (c.last_message_preview || "").toLowerCase().includes(q),
    );
  }, [convs, search]);

  return (
    <div className="h-[calc(100dvh-3.5rem)] flex min-h-0" data-testid="whatsapp-page">
      {/* LISTA */}
      <aside className="w-80 border-r border-zinc-800/60 bg-[#0c0c10] flex flex-col">
        <div className="p-3 border-b border-zinc-800/60 flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-emerald-400" />
          <h1 className="font-semibold flex-1">WhatsApp</h1>
          <Button size="icon" variant="ghost" onClick={load} title="Refrescar"
            className="h-7 w-7 text-zinc-400 hover:text-zinc-100" data-testid="wa-refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button size="icon" variant="ghost" onClick={() => setShowCompose(true)} title="Iniciar conversación"
            className="h-7 w-7 text-emerald-400 hover:text-emerald-300" data-testid="wa-new">
            <Plus className="w-4 h-4" />
          </Button>
        </div>
        <div className="p-2.5 border-b border-zinc-800/60">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-zinc-500" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar…" className="bg-zinc-900 border-zinc-800 pl-7 h-9 text-sm" data-testid="wa-search" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto" data-testid="wa-conv-list">
          {loading ? (
            <div className="flex justify-center py-8 text-zinc-500"><Loader2 className="w-4 h-4 animate-spin" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10 text-zinc-500 text-xs">
              {convs.length === 0 ? "Aún no hay conversaciones. Escribe a +34 854 80 20 24 desde tu WhatsApp para iniciar." : "Sin coincidencias"}
            </div>
          ) : filtered.map((c) => (
            <div key={c.id} className={`group relative border-b border-zinc-900/80 hover:bg-zinc-900/60 transition-colors ${
                selectedId === c.id ? "bg-zinc-900/80" : ""}`}>
              <button onClick={() => setSelectedId(c.id)}
                data-testid={`wa-conv-${c.contact_wa_id}`}
                className="w-full text-left px-3 py-2.5">
                <div className="flex items-center gap-2 mb-0.5">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-zinc-100 truncate">
                      {c.contact_name || c.contact_profile_name || `+${c.contact_wa_id}`}
                    </div>
                    <div className="text-[10px] text-zinc-500 font-mono">+{c.contact_wa_id}</div>
                  </div>
                  {c.unread_count > 0 && (
                    <span className="bg-emerald-500 text-zinc-950 text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                      {c.unread_count}
                    </span>
                  )}
                </div>
                <div className="text-xs text-zinc-400 truncate pr-7">{c.last_message_preview || "—"}</div>
                <div className="flex items-center justify-between mt-0.5">
                  <div className="text-[10px] text-zinc-600">{c.last_message_at?.slice(0, 16).replace("T", " ") || ""}</div>
                  <div className={`text-[10px] uppercase tracking-wider ${
                    c.window_open ? "text-emerald-400" : "text-amber-400"}`}>
                    {c.window_open ? "Ventana abierta" : "Solo plantillas"}
                  </div>
                </div>
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); deleteConversation(c); }}
                className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-red-500/20 text-zinc-500 hover:text-red-400"
                title="Eliminar conversación"
                data-testid={`wa-delete-${c.contact_wa_id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      </aside>

      {/* HILO */}
      <main className="flex-1 flex flex-col bg-[#0a0a0c]">
        {selectedId ? (
          <ConversationThread key={selectedId} cid={selectedId} onUpdated={load} />
        ) : (
          <div className="flex-1 flex items-center justify-center text-zinc-500 text-sm">
            Selecciona una conversación
          </div>
        )}
      </main>

      <ComposeDialog open={showCompose} onClose={() => setShowCompose(false)}
        onSent={(cid) => { setShowCompose(false); load(); setTimeout(() => setSelectedId(cid), 300); }} />
    </div>
  );
}


function ConversationThread({ cid, onUpdated }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [showTemplate, setShowTemplate] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggesting, setSuggesting] = useState(false);
  const bottomRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/whatsapp/conversations/${cid}/messages`);
      setData(data);
    } catch (err) { toast.error("Error cargando hilo"); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    const id = setInterval(load, 12_000);
    return () => clearInterval(id);
    /* eslint-disable-next-line */
  }, [cid]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [data?.messages?.length]);

  const sugerir = async () => {
    if (suggesting) return;
    setSuggesting(true);
    setSuggestions([]);
    try {
      const { data: r } = await api.post(`/whatsapp/conversations/${cid}/sugerir-respuesta`);
      setSuggestions(r.sugerencias || []);
      if (!r.sugerencias || r.sugerencias.length === 0) {
        toast.error("El asistente no devolvió sugerencias. Reintenta.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error generando sugerencias");
    } finally { setSuggesting(false); }
  };

  const enviar = async (e) => {
    e?.preventDefault?.();
    if (!body.trim()) return;
    setSending(true);
    try {
      await api.post("/whatsapp/send-text", { conversation_id: cid, body });
      setBody("");
      await load();
      onUpdated?.();
    } catch (err) {
      const det = err?.response?.data?.detail || "Error enviando";
      if (det.includes("Ventana 24h")) {
        toast.error("Ventana 24h cerrada. Usa una plantilla.");
        setShowTemplate(true);
      } else { toast.error(det); }
    } finally { setSending(false); }
  };

  // Iter 85 — Adjuntar imagen/PDF: sube a Cloudinary y envía por WhatsApp.
  const [adjuntando, setAdjuntando] = useState(false);
  const fileInputRef = useRef(null);
  const onAdjuntar = () => fileInputRef.current?.click();
  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";  // permitir resubir el mismo archivo
    if (!file) return;
    if (file.size > 16 * 1024 * 1024) return toast.error("Máx 16MB");
    setAdjuntando(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/whatsapp/upload-media", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await api.post("/whatsapp/send-media", {
        conversation_id: cid,
        media_type: up.data.media_type,
        media_url: up.data.url,
        filename: up.data.filename,
        caption: body.trim() || null,
      });
      setBody("");
      await load();
      onUpdated?.();
      toast.success(up.data.media_type === "image" ? "Imagen enviada" : "Documento enviado");
    } catch (err) {
      const det = err?.response?.data?.detail || "Error adjuntando";
      if (det.includes("Ventana 24h")) {
        toast.error("Ventana 24h cerrada. Usa una plantilla.");
      } else { toast.error(det); }
    } finally { setAdjuntando(false); }
  };

  if (loading && !data) return <div className="flex-1 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-zinc-500" /></div>;
  if (!data) return null;

  const conv = data.conversation;
  return (
    <>
      <header className="px-4 py-3 border-b border-zinc-800/60 bg-[#0c0c10] flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-300 text-sm font-bold">
          {(conv.contact_name || conv.contact_profile_name || "?").slice(0, 1).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-zinc-100 truncate">
            {conv.contact_name || conv.contact_profile_name || `+${conv.contact_wa_id}`}
          </div>
          <div className="text-[10px] font-mono text-zinc-500">+{conv.contact_wa_id}</div>
        </div>
        <div className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${
          conv.window_open ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
            : "border-amber-500/40 text-amber-300 bg-amber-500/10"}`}>
          {conv.window_open ? "Ventana 24h abierta" : "Ventana cerrada — solo plantillas"}
        </div>
      </header>

      <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="wa-thread">
        {data.messages.length === 0 ? (
          <div className="text-center text-zinc-500 text-xs py-8">Sin mensajes todavía</div>
        ) : data.messages.map((m) => <MessageBubble key={m.id} m={m} />)}
        <div ref={bottomRef} />
      </div>

      <footer className="border-t border-zinc-800/60 bg-[#0c0c10] p-3">
        {!conv.window_open && !showTemplate && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded p-2 mb-2 text-xs text-amber-200 flex items-center gap-2" data-testid="wa-window-closed">
            <AlertCircle className="w-3.5 h-3.5 shrink-0" />
            <span className="flex-1">Han pasado más de 24h desde el último mensaje del contacto. Para reabrir, envía una plantilla aprobada.</span>
            <Button size="sm" onClick={() => setShowTemplate(true)} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 h-7 text-xs" data-testid="wa-use-template">
              Usar plantilla
            </Button>
          </div>
        )}
        {showTemplate ? (
          <TemplateSender cid={cid} onCancel={() => setShowTemplate(false)}
            onSent={() => { setShowTemplate(false); load(); onUpdated?.(); }} />
        ) : conv.window_open ? (
          <div className="space-y-2">
            {/* Sugerencias IA */}
            {suggestions.length > 0 && (
              <div className="space-y-1.5" data-testid="wa-suggestions">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-violet-300">
                  <Sparkles className="w-3 h-3" /> Sugerencias del asistente
                  <button onClick={() => setSuggestions([])}
                    className="ml-auto text-zinc-500 hover:text-zinc-300 normal-case text-xs">cerrar</button>
                </div>
                {suggestions.map((s, i) => (
                  <button key={i}
                    onClick={() => { setBody(s); setSuggestions([]); }}
                    data-testid={`wa-sugg-${i}`}
                    className="w-full text-left px-3 py-2 rounded border border-violet-500/30 bg-violet-500/10 hover:bg-violet-500/20 text-sm text-zinc-100 transition-colors">
                    {s}
                  </button>
                ))}
                <div className="text-[10px] text-zinc-500">
                  Pulsa una sugerencia para usarla. Puedes editarla antes de enviar.
                </div>
              </div>
            )}
            <form onSubmit={enviar} className="flex items-end gap-2">
              <input ref={fileInputRef} type="file"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                onChange={onFileChosen} className="hidden"
                data-testid="wa-file-input" />
              <Button type="button" onClick={onAdjuntar} disabled={adjuntando}
                title="Adjuntar imagen o PDF"
                className="bg-zinc-800 text-zinc-300 hover:bg-zinc-700 h-10"
                data-testid="wa-attach">
                {adjuntando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
              </Button>
              <textarea value={body} onChange={(e) => setBody(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); enviar(); }
                  else if (e.key === "Tab" && !body.trim()) {
                    e.preventDefault();
                    sugerir();
                  }
                }}
                placeholder="Escribe tu mensaje…  ·  Tab = sugerencia IA"
                rows={2}
                className="flex-1 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100 resize-none focus:outline-none focus:ring-1 focus:ring-emerald-500"
                data-testid="wa-body" />
              <Button type="button" onClick={sugerir} disabled={suggesting}
                title="Sugerir respuesta IA (Tab)"
                className="bg-violet-600 text-white hover:bg-violet-500 h-10"
                data-testid="wa-suggest">
                {suggesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              </Button>
              <Button type="submit" disabled={sending || !body.trim()}
                className="bg-emerald-500 text-zinc-950 hover:bg-emerald-400 h-10" data-testid="wa-send">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </form>
          </div>
        ) : null}
      </footer>
    </>
  );
}


function MessageBubble({ m }) {
  const inbound = m.direction === "inbound";
  const cls = inbound
    ? "bg-zinc-800/80 text-zinc-100 self-start rounded-tl-sm"
    : "bg-emerald-600/30 text-zinc-100 self-end rounded-tr-sm border border-emerald-500/30";
  return (
    <div className={`flex ${inbound ? "justify-start" : "justify-end"}`}>
      <div className={`max-w-[70%] rounded-lg px-3 py-2 text-sm ${cls}`} data-testid={`wa-msg-${m.id}`}>
        {m.type === "template" && (
          <div className="text-[10px] uppercase tracking-wider text-amber-300 mb-1">
            Plantilla · {m.template_name}
          </div>
        )}

        {/* Iter 85 — preview de media (imagen/PDF). En OUTBOUND `media_id`
            es ya la URL de Cloudinary; en INBOUND es el id de Meta. */}
        {m.type === "image" && (m.media_id || m.media_url) && (m.media_id?.startsWith?.("http") || m.media_url) && (
          <a href={m.media_id?.startsWith?.("http") ? m.media_id : m.media_url} target="_blank" rel="noopener noreferrer" className="block mb-1">
            <img src={m.media_id?.startsWith?.("http") ? m.media_id : m.media_url}
              alt={m.media_caption || "imagen"}
              className="rounded max-w-full max-h-64 object-contain" />
          </a>
        )}
        {m.type === "document" && (m.media_id?.startsWith?.("http") || m.media_url) && (
          <a href={m.media_id?.startsWith?.("http") ? m.media_id : m.media_url} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-2 px-2 py-1.5 mb-1 rounded bg-zinc-900/50 hover:bg-zinc-900 border border-zinc-700 text-amber-300 hover:text-amber-200">
            <FileText className="w-4 h-4 flex-shrink-0" />
            <span className="text-xs truncate">{m.media_filename || "documento.pdf"}</span>
          </a>
        )}
        {/* Inbound media sin URL pública aún (media_id es ID Meta): aviso */}
        {["image","document","video","audio"].includes(m.type) && m.media_id && !m.media_id?.startsWith?.("http") && !m.media_url && (
          <div className="flex items-center gap-1.5 text-xs text-zinc-400 mb-1">
            {m.type === "image" ? <ImageIcon className="w-3.5 h-3.5"/> : <FileText className="w-3.5 h-3.5"/>}
            <span>
              {m.media_error
                ? `[${m.type} — error: ${m.media_error}]`
                : `[${m.type} — descargando…]`}
            </span>
          </div>
        )}

        {m.text || (!["image","document"].includes(m.type) && <span className="italic text-zinc-400">[{m.type}]</span>)}
        {m.media_url && !m.media_id?.startsWith?.("http") && m.type !== "image" && m.type !== "document" && (
          <a href={m.media_url} target="_blank" rel="noopener noreferrer"
            className="text-amber-300 hover:text-amber-200 text-xs block mt-1">📎 ver adjunto</a>
        )}
        <div className="flex items-center justify-end gap-1 mt-1 text-[10px] text-zinc-500">
          <span>{m.timestamp?.slice(11, 16)}</span>
          {!inbound && m.sent_by_user_name && <span className="text-zinc-500">· {m.sent_by_user_name}</span>}
          {!inbound && m.status === "read" && <CheckCheck className="w-3 h-3 text-emerald-400" />}
          {!inbound && m.status === "delivered" && <CheckCheck className="w-3 h-3" />}
          {!inbound && m.status === "sent" && <Check className="w-3 h-3" />}
          {!inbound && m.status === "failed" && <AlertCircle className="w-3 h-3 text-red-400" />}
        </div>
      </div>
    </div>
  );
}


function TemplateSender({ cid, onSent, onCancel }) {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState("");
  const [language, setLanguage] = useState("");
  const [params, setParams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/whatsapp/templates").then(({ data }) => {
      setTemplates(data || []);
    }).catch(() => toast.error("Error cargando plantillas")).finally(() => setLoading(false));
  }, []);

  const tpl = templates.find((t) => `${t.name}|${t.language}` === selected);
  const bodyComponent = tpl?.components?.find((c) => c.type === "BODY");
  const nParams = bodyComponent?.text ? (bodyComponent.text.match(/{{\d+}}/g) || []).length : 0;

  useEffect(() => {
    if (tpl) setLanguage(tpl.language);
    setParams(Array(nParams).fill(""));
    // eslint-disable-next-line
  }, [selected]);

  const enviar = async () => {
    if (!tpl) return toast.error("Selecciona una plantilla");
    setSending(true);
    try {
      await api.post("/whatsapp/send-template", {
        conversation_id: cid, template_name: tpl.name, language: tpl.language,
        params: params.filter((p) => p !== undefined),
      });
      toast.success("Plantilla enviada");
      onSent?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error enviando"); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-2" data-testid="wa-template-sender">
      <div className="grid grid-cols-2 gap-2">
        <div className="col-span-2">
          <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Plantilla aprobada</Label>
          {loading ? (
            <div className="text-xs text-zinc-500">Cargando…</div>
          ) : (
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger className="bg-zinc-900 border-zinc-800 h-9 text-sm" data-testid="wa-tpl-select">
                <SelectValue placeholder="Selecciona…" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                {templates.map((t) => (
                  <SelectItem key={`${t.name}-${t.language}`} value={`${t.name}|${t.language}`}>
                    {t.name} [{t.language}] · {t.category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        {tpl && bodyComponent && (
          <div className="col-span-2 bg-zinc-900/60 border border-zinc-800 rounded p-2 text-xs text-zinc-300">
            <div className="text-[10px] uppercase text-zinc-500 mb-1">Vista previa cuerpo</div>
            <div className="whitespace-pre-wrap">{bodyComponent.text}</div>
          </div>
        )}
        {nParams > 0 && (
          <div className="col-span-2 grid grid-cols-2 gap-2">
            {Array.from({ length: nParams }).map((_, i) => (
              <div key={i}>
                <Label className="text-[10px] text-zinc-500">Variable {`{{${i + 1}}}`}</Label>
                <Input value={params[i] || ""}
                  onChange={(e) => setParams(params.map((p, j) => j === i ? e.target.value : p))}
                  className="bg-zinc-900 border-zinc-800 h-8 text-xs"
                  data-testid={`wa-tpl-param-${i + 1}`} />
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="flex gap-2 justify-end">
        <Button variant="ghost" onClick={onCancel} className="text-zinc-400 hover:text-zinc-100">Cancelar</Button>
        <Button onClick={enviar} disabled={sending || !tpl}
          className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="wa-tpl-send">
          {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
          Enviar plantilla
        </Button>
      </div>
    </div>
  );
}


function ComposeDialog({ open, onClose, onSent }) {
  const [phone, setPhone] = useState("");
  const [showTemplate, setShowTemplate] = useState(false);

  if (!open) return null;
  const norm = phone.replace(/[+\s-]/g, "");
  const canStart = norm.length >= 8;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-zinc-950 border border-zinc-800 rounded-xl p-5 w-full max-w-md text-zinc-100"
        data-testid="wa-compose">
        <h2 className="text-base font-semibold mb-1">Iniciar conversación</h2>
        <p className="text-xs text-zinc-500 mb-3">
          Como el contacto aún no ha escrito, hay que iniciar con una plantilla aprobada.
        </p>
        <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Número (incluye prefijo, ej. 34666123456)</Label>
        <Input value={phone} onChange={(e) => setPhone(e.target.value)}
          placeholder="34666123456" className="bg-zinc-900 border-zinc-800 mb-3 font-mono" data-testid="wa-compose-phone" />
        {!showTemplate && (
          <Button disabled={!canStart} onClick={() => setShowTemplate(true)}
            className="w-full bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="wa-compose-next">
            Siguiente → elegir plantilla
          </Button>
        )}
        {showTemplate && (
          <NewConvTemplateSender to={norm} onSent={onSent} onCancel={() => setShowTemplate(false)} />
        )}
      </div>
    </div>
  );
}


function NewConvTemplateSender({ to, onSent, onCancel }) {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState("");
  const [params, setParams] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/whatsapp/templates").then(({ data }) => setTemplates(data || []));
  }, []);

  const tpl = templates.find((t) => `${t.name}|${t.language}` === selected);
  const bodyComponent = tpl?.components?.find((c) => c.type === "BODY");
  const nParams = bodyComponent?.text ? (bodyComponent.text.match(/{{\d+}}/g) || []).length : 0;

  useEffect(() => { setParams(Array(nParams).fill("")); }, [selected, nParams]);

  const enviar = async () => {
    if (!tpl) return;
    setSending(true);
    try {
      const { data } = await api.post("/whatsapp/send-template", {
        to, template_name: tpl.name, language: tpl.language,
        params: params.filter((p) => p !== undefined),
      });
      toast.success("Plantilla enviada");
      onSent?.(data.conversation_id);
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-2 mt-2">
      <Select value={selected} onValueChange={setSelected}>
        <SelectTrigger className="bg-zinc-900 border-zinc-800 h-9 text-sm" data-testid="wa-compose-tpl">
          <SelectValue placeholder="Plantilla aprobada" />
        </SelectTrigger>
        <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
          {templates.map((t) => (
            <SelectItem key={`${t.name}-${t.language}`} value={`${t.name}|${t.language}`}>
              {t.name} [{t.language}]
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {bodyComponent && (
        <div className="bg-zinc-900/60 border border-zinc-800 rounded p-2 text-xs text-zinc-300">
          {bodyComponent.text}
        </div>
      )}
      {nParams > 0 && Array.from({ length: nParams }).map((_, i) => (
        <Input key={i} placeholder={`Variable {{${i + 1}}}`}
          value={params[i] || ""}
          onChange={(e) => setParams(params.map((p, j) => j === i ? e.target.value : p))}
          className="bg-zinc-900 border-zinc-800 h-8 text-xs" />
      ))}
      <div className="flex gap-2 justify-end">
        <Button variant="ghost" onClick={onCancel} className="text-zinc-400 hover:text-zinc-100">Atrás</Button>
        <Button onClick={enviar} disabled={sending || !tpl}
          className="bg-emerald-500 text-zinc-950 hover:bg-emerald-400" data-testid="wa-compose-send">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enviar y abrir conversación"}
        </Button>
      </div>
    </div>
  );
}
